import { useCallback, useEffect, useMemo } from 'react'

import { CursorState } from '@editor/config/constants/cursor'
import { EditorConfig } from '@editor/interfaces/editor'
import { BlueprintEditorTools } from '@editor/pages/projects/blueprint-editor/definitions'
import { removeDrawings } from '@editor/pages/projects/blueprint-editor/store/drawings'
import { RootState, useAppDispatch } from '@editor/store/app'
import { setCursor, setWorkingElementId } from '@editor/store/tool'
import { useSelector } from 'react-redux'

import { WORKING_ID } from '../constants'
import { reset } from '../store'
import useUndo from './useUndo'

export default function useEditor(): EditorConfig {
  // Hooks
  const { canUndo } = useUndo()

  // Store
  const dispatch = useAppDispatch()
  const selectedTool = useSelector((state: RootState) => state.blueprint.editor.selectedTool)
  const prevSelectedTool = useSelector((state: RootState) => state.blueprint.editor.prevSelectedTool)
  const workingElementId = useSelector((state: RootState) => state.toolUtil.core.workingElementId)

  // Vars
  const isToolSelected = useMemo(() => selectedTool === BlueprintEditorTools.Polygon, [selectedTool])
  const isPreviousTool = useCallback((tool: BlueprintEditorTools) => prevSelectedTool === tool, [prevSelectedTool])
  const hasWorking = useMemo(() => workingElementId === WORKING_ID, [workingElementId])

  /**
   * Reset working items when tool is unselected
   */
  useEffect(() => {
    if (isToolSelected) {
      dispatch(setCursor(CursorState.CROSSHAIR))
    } else if (isPreviousTool(BlueprintEditorTools.Polygon) && selectedTool !== BlueprintEditorTools.Focus) {
      dispatch(reset())
      dispatch(setCursor(CursorState.DEFAULT))
      if (hasWorking && workingElementId) {
        dispatch(removeDrawings([workingElementId]))
        dispatch(setWorkingElementId(undefined))
      }
    }
  }, [isToolSelected, selectedTool, hasWorking, workingElementId, isPreviousTool, dispatch])

  /**
   * Reset on unmount
   */
  useEffect(
    () => () => {
      dispatch(reset())
    },
    [dispatch],
  )

  return {
    buttons: {
      reset: {
        onClick: useCallback(() => dispatch(reset()), [dispatch]),
        isShown: useCallback(() => isToolSelected, [isToolSelected]),
        isDisabled: useCallback(() => !canUndo, [canUndo]),
      },
      undo: {
        isShown: useCallback(() => isToolSelected, [isToolSelected]),
        isDisabled: useCallback(() => !canUndo, [canUndo]),
      },
    },
  }
}
